import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { msalInstance } from './msalInstance';

import Home from './Home';
import Login from './components/Login';
import { hasRegisteredProfile } from './utils/toolPermissions';
import { NotificationProvider } from './notifications';

const CadSuppliers = lazy(() => import('./components/CadSuppliers'));
const CadPrestador = lazy(() => import('./components/CadPrestador'));
const AdminTools = lazy(() => import('./pages/AdminTools'));
const AprovSuppliers = lazy(() => import('./features/aprovacao-compras/AprovSuppliers'));
const AprovContabil = lazy(() => import('./features/aprovacao-contabil/AprovContabil'));
const MinhasRequisicoes = lazy(() => import('./pages/MinhasRequisicoes'));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#E6EAE1] to-[#E6E6F6]" role="status" aria-label="Carregando">
      <div className="h-10 w-10 rounded-full border-2 border-[#993399] border-t-transparent animate-spin" />
    </div>
  );
}

// ==========================================
// COMPONENTE DE BARREIRA (PROTECTED ROUTE)
// ==========================================
const ProtectedRoute = ({
  children,
  allowedRoles,
  requireRegistered = false,
}: {
  children: React.ReactElement;
  allowedRoles?: string[];
  requireRegistered?: boolean;
}) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const userEmail = localStorage.getItem('user_email');
  const userDept = localStorage.getItem('user_departamento');

  if (inProgress !== InteractionStatus.None) {
    return null;
  }

  // 1. Barreira de Autenticação (SSO Azure — tenant org)
  if (!isAuthenticated || !userEmail) {
    return <Navigate to="/Login" replace />;
  }

  // 2. Cadastro no sistema (core_usuarios)
  if (requireRegistered && !hasRegisteredProfile(userDept)) {
    return <Navigate to="/Home" replace />;
  }

  // 3. Barreira de Autorização por Setor
  if (allowedRoles) {
    if (!hasRegisteredProfile(userDept)) {
      return <Navigate to="/Home" replace />;
    }
    if (!allowedRoles.includes(userDept!) && userDept !== 'admin') {
      return <Navigate to="/Home" replace />;
    }
  }

  return children;
};

// ==========================================
// ROTAS PRINCIPAIS DA APLICAÇÃO
// ==========================================
export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
    <BrowserRouter>
    <NotificationProvider>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Rotas Públicas (Qualquer um acessa para tentar logar) */}
        <Route path="/" element={<Login />} />
        <Route path="/Login" element={<Login />} />
        <Route
          path="/minhas-requisicoes"
          element={
            <ProtectedRoute requireRegistered>
              <MinhasRequisicoes />
            </ProtectedRoute>
          }
        />
        
        {/* Home: SSO autenticado (conversores para todos) */}
        <Route 
          path="/Home" 
          element={<ProtectedRoute><Home /></ProtectedRoute>} 
        />
        <Route 
          path="/Cadastro" 
          element={
            <ProtectedRoute>
              <CadSuppliers />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/cadastro" 
          element={
            <ProtectedRoute>
              <CadSuppliers />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/CadPrestador" 
          element={
            <ProtectedRoute>
              <CadPrestador />
            </ProtectedRoute>
          } 
        />

        {/* Rotas Privadas Restritas (Apenas departamentos específicos acessam) */}
        <Route 
          path="/Admin" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminTools />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/AprovSuppliers" 
          element={
            <ProtectedRoute allowedRoles={['compras']}>
              <AprovSuppliers />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/AprovContabil" 
          element={
            <ProtectedRoute allowedRoles={['contabilidade']}>
              <AprovContabil />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </Suspense>
    </NotificationProvider>
    </BrowserRouter>
    </MsalProvider>
  );
}